"use client";

import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ code: string; name: string }>;
  placeholder?: string;
  icon?: React.ReactNode;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Search and select...",
  icon,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search text
  useEffect(() => {
    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchText, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.name === value);

  const handleSelect = (optionName: string) => {
    onValueChange(optionName);
    setIsOpen(false);
    setSearchText("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchText("");
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <span className="truncate text-sm">
            {selectedOption ? selectedOption.name : placeholder}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedOption && (
            <button
              onClick={handleClear}
              className="p-0 hover:bg-muted rounded"
              type="button"
              title="Clear selection"
              aria-label="Clear selection"
            >
              <X className="w-4 h-4 opacity-50 hover:opacity-100" />
            </button>
          )}
          <div className="w-4 h-4 opacity-50 flex items-center justify-center">
            ▼
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-1 z-50 p-2 border border-input bg-background shadow-lg">
          {/* Search Input */}
          <div className="mb-2">
            <Input
              ref={inputRef}
              placeholder="Type to search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-9 text-sm"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <ul className="space-y-1">
                {filteredOptions.map((option) => (
                  <li key={option.code}>
                    <button
                      onClick={() => handleSelect(option.name)}
                      className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-accent transition-colors ${
                        value === option.name ? "bg-accent font-semibold" : ""
                      }`}
                      type="button"
                    >
                      {option.name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">
                No results found
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
